import { useEffect, useState, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { DIMENSOES } from "@/lib/constants";
import {
  ArrowLeft, Loader2, FileSpreadsheet, Presentation, Trophy,
  ChevronDown, ChevronRight, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 }));
const sitBadge = (s) =>
  s === "APTO" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
  : s === "NÃO APTO" ? "bg-red-500/20 text-red-300 border-red-500/40"
  : "bg-slate-500/20 text-slate-300 border-slate-500/40";

export default function PraticaConsolidado() {
  const { turmaId, oficinaId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [erro, setErro] = useState(false);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setErro(false);
    api.get(`/pratica/consolidado?turma_id=${turmaId}&oficina_id=${oficinaId}`).then((r) => setData(r.data)).catch(() => setErro(true));
  }, [turmaId, oficinaId]);

  if (erro) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">Não foi possível carregar o consolidado.</p>
      <button data-testid="pcons-voltar" onClick={() => navigate("/avaliar")} className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"><ArrowLeft className="h-4 w-4" /> Voltar</button>
    </div>
  );
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const criterios = data.criterios || [];
  const exportar = (kind) => {
    const base = (data.turma?.nome || "turma").replace(/\s+/g, "_");
    const url = kind === "debriefing"
      ? `/pratica/debriefing/export/xlsx?turma_id=${turmaId}&oficina_id=${oficinaId}`
      : `/pratica/consolidado/export/xlsx?turma_id=${turmaId}&oficina_id=${oficinaId}`;
    downloadFile(url, `MÉTRON_${kind === "debriefing" ? "Debriefing" : "Consolidado"}_${base}.xlsx`).catch(() => toast.error("Falha ao exportar"));
  };

  return (
    <div>
      <button data-testid="pcons-voltar" onClick={() => navigate("/avaliar")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>
      <PageHeader title="Consolidado da Prática" subtitle={`${data.curso_nome || ""} · ${data.turma?.nome || ""} · ${data.oficina?.nome || ""}`}>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" data-testid="pcons-export-xlsx" onClick={() => exportar("consolidado")}>
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
          </Button>
          <Button size="sm" data-testid="pcons-debriefing" onClick={() => exportar("debriefing")}>
            <Presentation className="h-4 w-4 mr-2" /> Debriefing
          </Button>
        </div>
      </PageHeader>

      {/* Ranking */}
      <Card className="p-5 bg-card border-border mb-6">
        <div className="flex items-center gap-2 mb-4"><Trophy className="h-5 w-5 text-primary" /><h2 className="font-heading text-lg font-bold uppercase tracking-tight">Ranking</h2></div>
        {!data.ranking.length ? <p className="text-sm text-muted-foreground">Nenhuma avaliação registrada ainda.</p> : (
          <div className="space-y-2">
            {data.ranking.map((a, i) => (
              <div key={a.aluno_id} data-testid={`prank-${a.aluno_id}`} className="flex items-center gap-3">
                <div className="w-7 text-center font-heading font-bold text-muted-foreground">{i + 1}º</div>
                <div className="flex-1 min-w-0 truncate font-medium">{a.aluno_nome}</div>
                {a.situacao_final && <Badge className={sitBadge(a.situacao_final)}>{a.situacao_final}</Badge>}
                <Badge className="bg-secondary text-secondary-foreground border-border">{fmt(a.media_final)}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Por aluno */}
      <Card className="bg-card border-border overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-border font-heading font-bold uppercase tracking-tight text-sm">Por aluno</div>
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Aluno</TableHead>
              <TableHead className="text-center">Avaliadores</TableHead>
              <TableHead className="text-center hidden md:table-cell">Divergência</TableHead>
              <TableHead className="text-right">Média final</TableHead>
              <TableHead className="text-right">Situação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.alunos.map((al) => (
              <Fragment key={al.aluno_id}>
                <TableRow data-testid={`pcons-aluno-${al.aluno_id}`} className="border-border cursor-pointer" onClick={() => setOpen(open === al.aluno_id ? null : al.aluno_id)}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-1">
                      {open === al.aluno_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {al.aluno_nome}
                      {!!al.penalidades.length && <AlertTriangle className="h-3.5 w-3.5 text-orange-400" title="Possui ocorrências" />}
                    </span>
                  </TableCell>
                  <TableCell className="text-center text-muted-foreground">{al.n_avaliadores}</TableCell>
                  <TableCell className="text-center hidden md:table-cell">{fmt(al.divergencia)}</TableCell>
                  <TableCell className="text-right"><Badge className="bg-secondary text-secondary-foreground border-border">{fmt(al.media_final)}</Badge></TableCell>
                  <TableCell className="text-right">{al.situacao_final ? <Badge className={sitBadge(al.situacao_final)}>{al.situacao_final}</Badge> : "—"}</TableCell>
                </TableRow>
                {open === al.aluno_id && (
                  <TableRow className="border-border bg-secondary/30 hover:bg-secondary/30">
                    <TableCell colSpan={5}>
                      {!al.avaliadores.length ? <p className="text-xs text-muted-foreground py-2">Sem avaliações.</p> : (
                        <div className="overflow-x-auto py-2">
                          <table className="text-xs w-full min-w-[640px]">
                            <thead><tr className="text-muted-foreground">
                              <th className="text-left font-medium pr-3 py-1">Avaliador</th>
                              <th className="text-left font-medium pr-3">Status</th>
                              {criterios.map((c) => <th key={c.id} className="px-1 py-1 font-medium" title={c.nome}>{c.nome.slice(0, 10)}…</th>)}
                              <th className="px-2 font-medium">Média</th>
                              <th className="px-2 font-medium">Situação</th>
                            </tr></thead>
                            <tbody>
                              {al.avaliadores.map((av, idx) => (
                                <tr key={idx} className="border-t border-border/50">
                                  <td className="pr-3 py-1.5 font-medium whitespace-nowrap">{av.avaliador_nome}</td>
                                  <td className="pr-3">{av.status === "concluida" ? <span className="text-teal-300">Concluída</span> : <span className="text-orange-300">Em andamento</span>}</td>
                                  {criterios.map((c) => <td key={c.id} className="text-center px-1">{av.notas?.[c.id] ?? "—"}</td>)}
                                  <td className="text-center px-2 font-mono">{fmt(av.media_geral)}</td>
                                  <td className="text-center px-2">{av.situacao ? <Badge className={`${sitBadge(av.situacao)} text-[10px]`}>{av.situacao}</Badge> : "—"}</td>
                                </tr>
                              ))}
                              <tr className="border-t border-border font-semibold">
                                <td className="pr-3 py-1.5" colSpan={2}>Média por critério</td>
                                {criterios.map((c) => <td key={c.id} className="text-center px-1">{fmt(al.media_por_criterio?.[c.id])}</td>)}
                                <td className="text-center px-2">{fmt(al.media_final)}</td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                          {!!al.penalidades.length && (
                            <div className="mt-3 text-xs">
                              <span className="text-orange-300 font-medium">Ocorrências:</span>{" "}
                              {al.penalidades.map((p, i) => (
                                <Badge key={i} className="bg-orange-500/20 text-orange-300 border-orange-500/40 mr-1">
                                  {p.comportamento} (−{p.valor} · {p.status})
                                </Badge>
                              ))}
                              <div className="text-muted-foreground mt-1">Penalidades não descontam a média automaticamente (aguardando regra oficial).</div>
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
            {!data.alunos.length && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Nenhum aluno nesta turma.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* Distribuição de conceitos + média por critério */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 bg-card border-border">
          <h3 className="font-heading text-lg font-bold uppercase tracking-tight mb-3">Distribuição de conceitos</h3>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((n) => {
              const total = Object.values(data.distribuicao).reduce((a, b) => a + b, 0) || 1;
              const q = data.distribuicao[String(n)] || 0;
              return (
                <div key={n} data-testid={`pdist-${n}`} className="flex items-center gap-2 text-sm">
                  <div className="w-28 shrink-0 text-muted-foreground">{n} · {data.conceito_labels[String(n)]}</div>
                  <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary" style={{ width: `${(q / total) * 100}%` }} /></div>
                  <div className="w-8 text-right font-mono">{q}</div>
                </div>
              );
            })}
          </div>
          <div className="text-xs text-muted-foreground mt-3">Itens não observados: <span className="text-foreground font-medium">{data.total_nao_observados}</span></div>
        </Card>

        <Card className="bg-card border-border overflow-hidden">
          <div className="px-5 py-3 border-b border-border font-heading font-bold uppercase tracking-tight text-sm">Média da turma por critério</div>
          <Table>
            <TableHeader><TableRow className="border-border hover:bg-transparent"><TableHead>Critério</TableHead><TableHead className="hidden sm:table-cell">Dimensão</TableHead><TableHead className="text-right">Média</TableHead></TableRow></TableHeader>
            <TableBody>
              {criterios.map((c) => (
                <TableRow key={c.id} className="border-border">
                  <TableCell className="font-medium">{c.nome}</TableCell>
                  <TableCell className="hidden sm:table-cell"><Badge className={`${DIMENSOES[c.dimensao]?.badge} text-[10px]`}>{DIMENSOES[c.dimensao]?.label}</Badge></TableCell>
                  <TableCell className="text-right"><Badge className="bg-secondary text-secondary-foreground border-border">{fmt(data.media_geral_criterio?.[c.id])}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
